import React from 'react';
import type { PublicPassportHuntCheckpoint, PublicPassportHuntsPayload } from '../../types/event';

const OBJECT_KIND_LABELS: Record<string, string> = {
  checkpoint: 'Checkpoint',
  booth: 'Booth',
  activation: 'Activation',
  destination: 'Destination',
};

function objectKindLabel(kind: string): string {
  return OBJECT_KIND_LABELS[kind] || 'Checkpoint';
}

interface EventPublicPassportHuntsProps {
  hunts?: PublicPassportHuntsPayload | null;
}

const EventPublicPassportHunts: React.FC<EventPublicPassportHuntsProps> = ({ hunts }) => {
  const visibility = hunts?.visibility || 'hidden';
  if (visibility === 'hidden') return null;

  const items = hunts?.hunts || [];
  if (items.length === 0) return null;

  return (
    <section className="mb-8" aria-labelledby="event-public-passport-hunts-heading">
      <h2 id="event-public-passport-hunts-heading" className="heading-section mb-2">
        Passport hunts
      </h2>
      <p className="text-muted text-sm mb-4 leading-relaxed">
        Explore what is at this event. Scan codes unlock when you join a hunt in Passport.
      </p>
      <div className="space-y-4">
        {items.map((hunt) => (
          <article
            key={hunt.id}
            className="border border-white/[0.06] rounded-xl overflow-hidden bg-[#0c0c0c]/40"
          >
            <div className="px-4 py-3.5 border-b border-white/[0.06]">
              <h3 className="font-medium text-white">{hunt.title}</h3>
              {hunt.description ? (
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">{hunt.description}</p>
              ) : null}
            </div>
            {hunt.checkpoints.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted">Checkpoints will be posted soon.</p>
            ) : (
              <ul className="divide-y divide-white/[0.06]">
                {hunt.checkpoints.map((checkpoint: PublicPassportHuntCheckpoint, index) => (
                  <li key={checkpoint.id} className="px-4 py-3.5">
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden
                        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/[0.12] bg-[#141414] text-xs text-gray-400"
                      >
                        {checkpoint.sequence_order ?? index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-wide text-orange-300/80">
                          {objectKindLabel(checkpoint.object_kind)}
                          {checkpoint.is_required ? ' · Required' : ''}
                        </p>
                        <p className="font-medium text-white mt-0.5">{checkpoint.title}</p>
                        {checkpoint.location_label ? (
                          <p className="text-sm text-gray-500 mt-0.5">{checkpoint.location_label}</p>
                        ) : null}
                        {checkpoint.description ? (
                          <p className="text-sm text-gray-400 mt-1 leading-relaxed">{checkpoint.description}</p>
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};

export default EventPublicPassportHunts;
